import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon, 
  PlusIcon, 
  UserIcon, 
  EnvelopeIcon,
  PhoneIcon,
  TrashIcon,
  PencilIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation } from '../../hooks/useApi';
import apiClient from '../../utils/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

interface User {
  id: string;
  email: string;
  full_name: string;
  mobile_number?: string;
  role: 'user' | 'manager' | 'admin';
  is_active: boolean;
  assigned_locations: string[];
  created_at: string;
}

interface Location {
  id: string;
  name: string;
}

interface UserFormData {
  email: string;
  password?: string;
  full_name: string;
  mobile_number: string;
  role: 'user' | 'manager' | 'admin';
  assigned_locations?: string[];
}

const userSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters'),
  full_name: yup.string().required('Full name is required'),
  mobile_number: yup
    .string()
    .required('Mobile number is required')
    .matches(/^[0-9+\-\s()]{10,15}$/, 'Enter a valid mobile number (10–15 digits)'),
  role: yup.string().required('Role is required'),
  assigned_locations: yup.array().of(yup.string()).when('role', {
    is: (role: string) => role === 'user' || role === 'manager',
    then: (schema) => schema.min(1, 'At least one location must be assigned'),
    otherwise: (schema) => schema.optional(),
  }),
});

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState('');

  const { mutate: createUser, loading: creating } = useMutation<User, UserFormData>();
  const isFreePlan = (user?.plan || 'free') === 'free';
  const activeUserCount = users.filter((u) => u.is_active).length;
  const freeUserLimitReached = isFreePlan && activeUserCount >= 1;
  
  const form = useForm<UserFormData>({
    resolver: yupResolver(userSchema) as any,
    defaultValues: {
      email: '',
      password: '',
      full_name: '',
      mobile_number: '',
      role: 'user' as const,
      assigned_locations: [],
    }
  });

  useEffect(() => {
    loadUsers();
    loadLocations();
  }, []);

  // Update form when editingUser changes
  useEffect(() => {
    if (editingUser) {
      form.reset({
        email: editingUser.email,
        password: '',
        full_name: editingUser.full_name,
        mobile_number: editingUser.mobile_number || '',
        role: editingUser.role,
        assigned_locations: editingUser.assigned_locations,
      });
    } else {
      form.reset({
        email: '',
        password: '',
        full_name: '',
        mobile_number: '',
        role: 'user' as const,
        assigned_locations: [],
      });
    }
    form.clearErrors();
  }, [editingUser, form]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get('/users');
      setUsers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await apiClient.get('/locations');
      setLocations(response.data);
    } catch (err: any) {
      console.error('Failed to load locations:', err);
    }
  };

  const validateForm = (data: UserFormData) => {
    const errors: any = {};
    
    // Password validation - required for new users, optional for editing
    if (!editingUser && (!data.password || data.password.length < 6)) {
      errors.password = 'Password is required and must be at least 6 characters';
    } else if (data.password && data.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    // Location validation for users and managers
    if ((data.role === 'user' || data.role === 'manager') && (!data.assigned_locations || data.assigned_locations.length === 0)) {
      errors.assigned_locations = 'At least one location must be assigned';
    }
    
    return Object.keys(errors).length > 0 ? errors : null;
  };

  const handleCreateUser = async (data: UserFormData) => {
    if (freeUserLimitReached) {
      setError('Free plan allows only 1 user. Upgrade to PRO to add managers and workers.');
      setShowCreateForm(false);
      return;
    }

    try {
      // Custom validation
      const validationErrors = validateForm(data);
      if (validationErrors) {
        Object.keys(validationErrors).forEach(key => {
          form.setError(key as any, { message: validationErrors[key] });
        });
        return;
      }

      // Ensure assigned_locations is an array for users and managers
      const submitData = {
        ...data,
        assigned_locations: (data.role === 'user' || data.role === 'manager') 
          ? (data.assigned_locations || [])
          : []
      };
      
      const newUser = await createUser('/auth/register', submitData);
      if (newUser) {
        setUsers([...users, newUser]);
        setShowCreateForm(false);
        form.reset();
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowCreateForm(true);
  };

  const handleUpdateUser = async (data: UserFormData) => {
    if (!editingUser) return;
    
    try {
      // Custom validation
      const validationErrors = validateForm(data);
      if (validationErrors) {
        Object.keys(validationErrors).forEach(key => {
          form.setError(key as any, { message: validationErrors[key] });
        });
        return;
      }

      const updateData = { ...data };
      if (!data.password) {
        delete (updateData as any).password; // Don't update password if empty
      }
      
      // Ensure assigned_locations is properly handled
      updateData.assigned_locations = (data.role === 'user' || data.role === 'manager') 
        ? (data.assigned_locations || [])
        : [];
      
      const response = await apiClient.put(`/users/${editingUser.id}`, updateData);
      setUsers(users.map(user => 
        user.id === editingUser.id ? response.data : user
      ));
      setShowCreateForm(false);
      setEditingUser(null);
      form.reset();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      await apiClient.patch(`/users/${userId}/toggle-status`);
      setUsers(users.map(user => 
        user.id === userId ? { ...user, is_active: !currentStatus } : user
      ));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update user status');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      case 'user':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return ShieldCheckIcon;
      case 'manager':
        return UserGroupIcon;
      case 'user':
        return UserIcon;
      default:
        return UserIcon;
    }
  };

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage user accounts, roles, and permissions</p>
          {freeUserLimitReached && (
            <p className="text-xs text-amber-700 mt-2">
              Free plan limit reached (1 user). Upgrade to PRO to add managers and workers.
            </p>
          )}
        </div>
        {!freeUserLimitReached && (
          <button
            onClick={() => {
              setEditingUser(null);
              form.reset();
              setShowCreateForm(true);
            }}
            className="flex items-center justify-center sm:justify-start px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add User
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {editingUser ? 'Edit User' : 'Create New User'}
          </h2>
          
          <form 
            onSubmit={form.handleSubmit(editingUser ? handleUpdateUser : handleCreateUser)}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  {...form.register('full_name')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter full name"
                />
                {form.formState.errors.full_name && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.full_name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  {...form.register('email')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter email address"
                  disabled={!!editingUser}
                />
                {form.formState.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  {...form.register('mobile_number')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. 9876543210"
                  autoComplete="tel"
                />
                {form.formState.errors.mobile_number && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.mobile_number.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password {editingUser ? '(leave blank to keep current)' : '*'}
                </label>
                <input
                  type="password"
                  {...form.register('password')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={editingUser ? "Leave blank to keep current password" : "Enter password"}
                />
                {form.formState.errors.password && (
                  <p className="mt-1 text-sm text-red-600">{form.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role *
                </label>
                <select
                  {...form.register('role')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="user">User (Read-only)</option>
                  <option value="manager">Manager (Can add/edit data)</option>
                  <option value="admin">Admin (Full access)</option>
                </select>
              </div>
            </div>

            {/* Location Assignment for Users and Managers */}
            {(form.watch('role') === 'user' || form.watch('role') === 'manager') && locations.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Locations *
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Select which farm locations this user can access. Users and managers can only see data from their assigned locations.
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {locations.map((location) => (
                    <label key={location.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        value={location.id}
                        {...form.register('assigned_locations')}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{location.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingUser(null);
                  form.reset();
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
              >
                {creating ? <LoadingSpinner size="sm" /> : (editingUser ? 'Update User' : 'Create User')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          System Users ({users.length})
        </h2>

        {users.length > 0 ? (
          <div className="space-y-3">
            {users.map((user) => {
              const RoleIcon = getRoleIcon(user.role);
              return (
                <div key={user.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start space-x-3 sm:space-x-4 min-w-0">
                      <div className={`p-3 rounded-lg ${getRoleColor(user.role)} bg-opacity-20`}>
                        <RoleIcon className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <h3 className="text-lg font-semibold text-gray-900 break-words">{user.full_name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-2 mt-2 min-w-0">
                          <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600 break-all">{user.email}</span>
                        </div>

                        {user.mobile_number && (
                          <div className="flex items-center space-x-2 mt-1 min-w-0">
                            <PhoneIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">{user.mobile_number}</span>
                          </div>
                        )}
                        
                        {user.assigned_locations && user.assigned_locations.length > 0 && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">
                              Assigned to: {user.assigned_locations.map(locId => {
                                const location = locations.find(loc => loc.id === locId);
                                return location ? location.name : 'Unknown';
                              }).join(', ')}
                            </span>
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-500 mt-2">
                          Created: {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center flex-wrap gap-2 sm:gap-2 sm:justify-end">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="p-2 rounded-md border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-200"
                        title="Edit user"
                        aria-label="Edit user"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                        className={`p-2 rounded-md border border-gray-200 ${user.is_active ? 'text-gray-500 hover:text-red-600 hover:border-red-200' : 'text-gray-500 hover:text-green-600 hover:border-green-200'}`}
                        title={user.is_active ? 'Deactivate user' : 'Activate user'}
                        aria-label={user.is_active ? 'Deactivate user' : 'Activate user'}
                      >
                        {user.is_active ? (
                          <TrashIcon className="h-4 w-4" />
                        ) : (
                          <UserIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Users Found</h3>
            <p className="text-gray-600 mb-4">Create user accounts to manage system access.</p>
            {!freeUserLimitReached && (
              <button
                onClick={() => {
                  setEditingUser(null);
                  form.reset();
                  setShowCreateForm(true);
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create First User
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;